import i18next from 'i18next'
import * as Yup from 'yup'
import {
	IncomeChainAssociationEnum,
	CategorizedDocument,
} from '../IncomeVerification.types'
import { ApplicantProvidedDocumentConfiguration } from '../../../types/authenticateSmartcheckSessionDto'

export const incomeVerificationSchema = (context: {
	expandAssociationOwnerQuestionaire: boolean
	mustUploadDocumentation: boolean
	allowFileUpload: boolean
	documentConfigurations: readonly ApplicantProvidedDocumentConfiguration[]
	maxFileSize: number
}) => {
	const hasDocumentCategories = context.documentConfigurations.length > 0
	const hasRequiredDocumentCategories = context.documentConfigurations.some(
		(conf) => conf.required,
	)
	return Yup.object().shape({
		incomeChains: Yup.array().of(
			Yup.object().shape({
				hasLargeInputDifferences: Yup.boolean(),
				owner: Yup.mixed()
					.oneOf(
						[
							IncomeChainAssociationEnum.Mine,
							IncomeChainAssociationEnum.Partner,
							IncomeChainAssociationEnum.Unknown,
						],
						i18next.t('validation-invalidOwner'),
					)
					.test(
						'owner-required-validation',
						i18next.t('validation-fieldRequired'),
						(owner) => {
							const { expandAssociationOwnerQuestionaire } = context
							return expandAssociationOwnerQuestionaire ? owner != null : true
						},
					),
				assessment: Yup.object().shape({
					value: Yup.number().nullable(),
					variability: Yup.string().nullable(),
				}),
				range: Yup.object()
					.shape({
						min: Yup.number().required(),
						max: Yup.number().required(),
					})
					.test(
						'max-min-validation',
						i18next.t(
							'incomeVerification-validation-maxShouldBeEqualOrBiggerThanMin',
						),
						(range) => range.min <= range.max,
					),
			}),
		),
		uploadedFiles:
			context.mustUploadDocumentation && !hasDocumentCategories
				? minimumNumberOfFiles(1, context.maxFileSize)
				: minimumNumberOfFiles(0, context.maxFileSize),
		categorizedDocuments: hasDocumentCategories
			? hasRequiredDocumentCategories &&
				(context.mustUploadDocumentation || context.allowFileUpload)
				? requiredFileCategoriesUploaded(
						context.documentConfigurations,
						context.maxFileSize,
					)
				: context.mustUploadDocumentation
					? minimumNumberOfCategorizedFiles(
							1,
							context.documentConfigurations,
							context.maxFileSize,
						)
					: minimumNumberOfCategorizedFiles(
							0,
							context.documentConfigurations,
							context.maxFileSize,
						)
			: minimumNumberOfFiles(0, context.maxFileSize),
	})
}

const minimumNumberOfFiles = (count: number, maxFileSize: number) =>
	Yup.array<Yup.AnyObject, CategorizedDocument>()
		.min(count, i18next.t('validation-fieldRequired'))
		.test(
			'file-size',
			(files) =>
				(files && files.every(([file]) => file.size < maxFileSize)) ?? false,
		)

const minimumNumberOfCategorizedFiles = (
	count: number,
	documentConfigurations: readonly ApplicantProvidedDocumentConfiguration[],
	maxFileSize: number,
) =>
	Yup.array<Yup.AnyObject, CategorizedDocument>()
		.min(count, i18next.t('validation-fieldRequired'))
		.test(
			'has-categorized-documents',
			i18next.t('validation-fieldRequired'),
			(categorizedDocuments) =>
				categorizedDocuments?.every(([_, type]) =>
					documentConfigurations.some(
						(conf) => conf.applicantProvidedDocumentType === type,
					),
				),
		)
		.test(
			'file-size',
			(files) =>
				(files && files.every(([file]) => file.size < maxFileSize)) ?? false,
		)

const requiredFileCategoriesUploaded = (
	documentConfigurations: readonly ApplicantProvidedDocumentConfiguration[],
	maxFileSize: number,
) =>
	Yup.array<Yup.AnyObject, CategorizedDocument>()
		.test(
			'has-categorized-documents',
			i18next.t('validation-fieldRequired'),
			(categorizedDocuments) =>
				documentConfigurations
					.filter((c) => c.required)
					.every((c) =>
						categorizedDocuments?.some(
							([_, type]) => c.applicantProvidedDocumentType === type,
						),
					),
		)
		.test(
			'file-size',
			(files) =>
				(files && files.every(([file]) => file.size < maxFileSize)) ?? false,
		)
